// src/lib/geodesy.ts — măsurători GEODEZICE pe WGS84 (REQ-PEX-004, REQ-FMT-004).
//
// De ce nu planar: coordonatele hărții sunt lng/lat în grade (EPSG:4326). Un „Pitagora pe grade"
// e greșit cu zeci de procente (la 45° latitudine, 1° de longitudine ≈ 79 km, nu 111 km), iar în
// Web Mercator ariile se umflă cu ~1/cos²(lat) — la latitudinea României, ~+100% (o parcelă de
// 1000 m² ar fi raportată ca ~2000 m²). Un urbanist care măsoară o parcelă nu poate primi așa ceva.
// Aici: haversine pentru distanțe, excesul sferic pentru arii (aceleași formule ca turf.js, fără
// dependința de 200 KB).
//
// Funcții PURE (fără DOM, fără MapLibre) → testabile direct: tests/geodesy.test.ts.

/** Raza medie a Pământului (IUGG), în metri — aceeași convenție ca turf/PostGIS geography. */
const EARTH_RADIUS_M = 6_371_008.8

/** Punct [longitudine, latitudine] în grade — ordinea GeoJSON (RFC 7946), NU lat/lng. */
export type LngLat = readonly [number, number]

function to_rad(deg: number): number {
	return (deg * Math.PI) / 180
}

/**
 * Distanța geodezică între două puncte (haversine), în metri.
 * Precizie: ~0.5% față de elipsoid — sub eroarea de digitizare a hărții, deci suficientă.
 */
export function distance_m(a: LngLat, b: LngLat): number {
	const [lng1, lat1] = a
	const [lng2, lat2] = b
	const d_lat = to_rad(lat2 - lat1)
	const d_lng = to_rad(lng2 - lng1)
	const s =
		Math.sin(d_lat / 2) ** 2 +
		Math.cos(to_rad(lat1)) * Math.cos(to_rad(lat2)) * Math.sin(d_lng / 2) ** 2
	return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(s)))
}

/** Lungimea cumulată a unei polilinii, în metri. Sub 2 puncte → 0. */
export function path_length_m(points: readonly LngLat[]): number {
	let total = 0
	for (let i = 1; i < points.length; i++) {
		total += distance_m(points[i - 1]!, points[i]!)
	}
	return total
}

/**
 * Aria geodezică a unui poligon (excesul sferic), în metri pătrați. Inelul se închide automat.
 * Sub 3 puncte → 0. Rezultatul e ABSOLUT (nu depinde de sensul de parcurgere).
 */
export function polygon_area_m2(points: readonly LngLat[]): number {
	if (points.length < 3) {
		return 0
	}
	let total = 0
	for (let i = 0; i < points.length; i++) {
		const p1 = points[i]!
		const p2 = points[(i + 1) % points.length]! // închide inelul
		total += to_rad(p2[0] - p1[0]) * (2 + Math.sin(to_rad(p1[1])) + Math.sin(to_rad(p2[1])))
	}
	return Math.abs((total * EARTH_RADIUS_M * EARTH_RADIUS_M) / 2)
}

/** Număr cu virgulă zecimală RO (1234.5 → „1.234,5"). */
function ro(n: number, decimals: number): string {
	return n.toLocaleString("ro-RO", {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
	})
}

/** Distanță lizibilă: sub 1 km în metri, peste în kilometri. */
export function format_distance(meters: number): string {
	return meters < 1000 ? `${ro(meters, 0)} m` : `${ro(meters / 1000, 2)} km`
}

/**
 * Arie lizibilă. Pragurile urmează practica urbanistică: parcelele se citesc în m², terenurile
 * mari în hectare (unitatea din actele de urbanism), suprafețele foarte mari în km².
 */
export function format_area(m2: number): string {
	if (m2 < 10_000) {
		return `${ro(m2, 0)} m²`
	}
	if (m2 < 1_000_000) {
		return `${ro(m2 / 10_000, 2)} ha`
	}
	return `${ro(m2 / 1_000_000, 2)} km²`
}
